import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Draw as DrawIcon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';
import axios from 'axios';
import SignaturePad from '../common/SignaturePad';

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;

const ProfileSignature = ({ userId, signatureImageUrl, readOnly = false, onUpdated }) => {
  const fileInputRef = useRef(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const closeDialog = () => {
    setDialogOpen(false);
    setError('');
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const notifyUpdated = () => {
    if (typeof onUpdated === 'function') {
      onUpdated();
    }
  };

  const handlePadSign = async (dataUrl) => {
    if (!userId) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`/api/users/${userId}/signature`, { signatureData: dataUrl });
      closeDialog();
      notifyUpdated();
    } catch (err) {
      console.error('Signature pad save error:', err);
      setError(err.response?.data?.message || 'Failed to save drawn signature.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFilePick = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError('Unsupported file type. Use PNG, JPG, or WEBP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('File is too large. Maximum size is 5 MB.');
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setError('');
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleUploadFile = async () => {
    if (!userId || !selectedFile) return;
    setSubmitting(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      await axios.post(`/api/users/${userId}/signature`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      closeDialog();
      notifyUpdated();
    } catch (err) {
      console.error('Signature file upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload signature image.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    if (!userId || !signatureImageUrl) return;
    if (!window.confirm('Remove the saved signature from this profile?')) return;

    setSubmitting(true);
    setError('');
    try {
      await axios.delete(`/api/users/${userId}/signature`);
      notifyUpdated();
    } catch (err) {
      console.error('Signature delete error:', err);
      setError(err.response?.data?.message || 'Failed to remove signature.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box mt={3}>
      <Typography variant="subtitle1" gutterBottom>
        Report signature
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Upload or draw a signature image to store on your profile. It can be mapped onto
        generated reports later.
      </Typography>

      {error && !dialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {signatureImageUrl ? (
        <Box
          sx={{
            display: 'inline-block',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: '#fff',
            p: 1.5,
            mb: 2
          }}
        >
          <Box
            component="img"
            src={signatureImageUrl}
            alt="Saved signature"
            sx={{
              display: 'block',
              maxWidth: 320,
              maxHeight: 120,
              objectFit: 'contain'
            }}
          />
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No signature saved yet.
        </Typography>
      )}

      {!readOnly && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            startIcon={signatureImageUrl ? <DrawIcon /> : <UploadFileIcon />}
            onClick={() => {
              setError('');
              setDialogOpen(true);
            }}
            disabled={submitting || !userId}
          >
            {signatureImageUrl ? 'Replace signature' : 'Add signature'}
          </Button>
          {signatureImageUrl && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleRemove}
              disabled={submitting}
            >
              Remove
            </Button>
          )}
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={submitting ? undefined : closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{signatureImageUrl ? 'Replace signature' : 'Add signature'}</DialogTitle>
        <DialogContent>
          <Tabs
            value={tab}
            onChange={(_, value) => {
              setTab(value);
              setError('');
            }}
            sx={{ mb: 2 }}
          >
            <Tab label="Draw" icon={<DrawIcon />} iconPosition="start" />
            <Tab label="Upload image" icon={<UploadFileIcon />} iconPosition="start" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {tab === 0 && (
            <Box sx={{ opacity: submitting ? 0.6 : 1, pointerEvents: submitting ? 'none' : 'auto' }}>
              <SignaturePad onSign={handlePadSign} />
            </Box>
          )}

          {tab === 1 && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Choose a clear PNG, JPG, or WEBP image of your signature (max 5 MB). Prefer a
                transparent or white background for report use.
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                hidden
                onChange={handleFilePick}
              />
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting}
              >
                Choose image
              </Button>
              {selectedFile && (
                <Typography variant="caption" color="text.secondary">
                  Selected: {selectedFile.name}
                </Typography>
              )}
              {previewUrl && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: '#fff',
                    p: 1.5,
                    display: 'inline-block',
                    alignSelf: 'flex-start'
                  }}
                >
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Signature preview"
                    sx={{ display: 'block', maxWidth: 320, maxHeight: 140, objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            Cancel
          </Button>
          {tab === 1 && (
            <Button
              variant="contained"
              onClick={handleUploadFile}
              disabled={submitting || !selectedFile}
            >
              {submitting ? 'Saving...' : 'Save signature'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileSignature;
